import { useTranslation } from 'react-i18next'
import { Button } from './Button'
import type { ReportLanguage } from '../utils/downloadParticipantReport'

const OPTIONS: ReportLanguage[] = ['en', 'ja']

/** Props for {@link RecipientLanguagePicker}. */
export interface RecipientLanguagePickerProps {
  /** Prefix for radio input ids (must be unique on the page). */
  id: string
  value: ReportLanguage
  onChange: (language: ReportLanguage) => void
  /** Hint under the control explaining what the choice affects. */
  hint: string
  disabled?: boolean
}

/**
 * Two-option English/Japanese picker for the language of mail sent to a participant
 * the organiser is inviting or registering on their behalf.
 */
export function RecipientLanguagePicker({
  id,
  value,
  onChange,
  hint,
  disabled = false,
}: RecipientLanguagePickerProps) {
  const { t } = useTranslation()
  const labelId = `${id}-label`
  const hintId = `${id}-hint`

  return (
    <div className="flex flex-col gap-1">
      <span id={labelId} className="text-pax-black text-xs font-medium">
        {t('recipient_lang_label')}
      </span>
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        aria-describedby={hintId}
        className="border-pax-purple/30 flex flex-wrap gap-2 rounded-xl border bg-white p-2"
      >
        {OPTIONS.map((code) => (
          <Button
            key={code}
            type="button"
            variant="unstyled"
            role="radio"
            aria-checked={value === code}
            disabled={disabled}
            onClick={() => onChange(code)}
            className={`inline-flex cursor-pointer items-center justify-center rounded-full px-3 py-1 text-xs font-semibold transition-all ${
              value === code
                ? 'bg-pax-blue ring-pax-blue text-white ring-2 ring-offset-1'
                : 'text-pax-black bg-pax-gray/10 opacity-70 hover:opacity-100'
            } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            {t(`lang_option_${code}`)}
          </Button>
        ))}
      </div>
      <p id={hintId} className="text-pax-gray text-xs">
        {hint}
      </p>
    </div>
  )
}
